import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux"; // Add this import if not already present
import {
    addShopToLicense,
    changeImageVersion,
    clearLicenseTseIssueActive,
    deleteInstance,
    deploy,
    getAvailableVersions,
    getBlsImportProgress,
    getLicenseById,
    importBlsNutritionalValues,
    reactivateLicense,
    revokeLicense,
    runLicenseTseHealthCheck,
    startInstance,
    stopInstance,
    updateLicense,
} from "../../../actions/licensesActions";
import { getAllModules } from "../../../actions/moduleActions";
import { deployShop, updateShop } from "../../../actions/shopActions";
import { getLicmanShopDeployAlertMessage } from "../../../helpers/shopDeployMessages";
import store from "../../../store";

const CATEGORIES = [
    "Bäckerei / Konditorei",
    "Bar / Kneipe",
    "Café / Eisdiele",
    "Hotel / Pension",
    "Imbiss / Street Food",
    "Kantine / Mensa",
    "Lebensmittelhändler / Feinkostladen",
    "Metzgerei / Fleischerei",
    "Pizzeria / Lieferdienst",
    "Restaurant / Gaststätte",
    "Sonstiges",
];

function License(props) {
    const navigate = useNavigate();
    const [licenseId] = useState(props.licenseId);
    const [license, setLicense] = useState(undefined);
    const [deployStatus, setDeployStatus] = useState(0);
    const [deployStatusString, setDeployStatusString] = useState("");
    const [modules, setModules] = useState([]);
    const [availableVersions] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState(undefined);
    const [showRevokeLicensePopup, setShowRevokeLicensePopup] = useState(false);
    const [showDeleteInstancePopup, setShowDeleteInstancePopup] = useState(false);
    const [showChangeVersionPopup, setShowChangeVersionPopup] = useState(false);
    const [revokedReason, setRevokedReason] = useState("");
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const [blsImportRunning, setBlsImportRunning] = useState(false);
    const [blsImportProgress, setBlsImportProgress] = useState(null);
    const [showBlsProgressPopup, setShowBlsProgressPopup] = useState(false);
    const [blsProgressPollingActive, setBlsProgressPollingActive] = useState(false);
    const [overwriteBlsExisting, setOverwriteBlsExisting] = useState(true);
    const [tseHealthDialogOpen, setTseHealthDialogOpen] = useState(false);
    const [tseHealthLoading, setTseHealthLoading] = useState(false);
    const [tseHealthResult, setTseHealthResult] = useState(null);
    /** Quittieren only after a completed health check with `ok: true` from fgfunctions */
    const [tseHealthCheckSucceeded, setTseHealthCheckSucceeded] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        setTseHealthCheckSucceeded(false);
    }, [licenseId]);

    React.useEffect(() => {
        dispatch(getAvailableVersions()); // Dispatch the action instead of calling the function directly
        dispatch(getAllModules()).then((response) => setModules(response));
        if (licenseId) {
            dispatch(getLicenseById(licenseId)).then((result) => {
                setLicense(result);
                setDeployStatus(result.Container ? result.deployStatus : 0);
                setDeployStatusString(result.Container ? result.deployStatusString : "");
                setSelectedVersion(result.containerImage);
            });
            store
                .dispatch(getBlsImportProgress(licenseId))
                .then((progress) => {
                    if (progress && progress.status) {
                        setBlsImportProgress(progress);
                        if (progress.status === "running") {
                            setBlsImportRunning(true);
                            setShowBlsProgressPopup(true);
                            setBlsProgressPollingActive(true);
                        }
                    }
                })
                .catch(() => {
                    // No active import found or progress not yet available.
                });
        }
    }, [licenseId, dispatch]);

    const removeByAttr = (arr, attr, value) => {
        var i = arr.length;
        while (i--) {
            if (arr[i] && arr[i].hasOwnProperty(attr) && arguments.length > 2 && arr[i][attr] === value) {
                arr.splice(i, 1);
            }
        }
        return arr;
    };

    const deployInstance = () => {
        setDeployStatusString("(1/4) Deploy gestartet");
        setDeployStatus(1);
        store.dispatch(deploy(licenseId, selectedVersion ? selectedVersion : "latest"));
    };

    const handleDeploy = (data) => {
        store.dispatch(deployShop(licenseId, data.shopId)).catch((e) => {
            window.alert(getLicmanShopDeployAlertMessage(e));
        });
    };

    const handleInsert = (e) => {
        store.dispatch(addShopToLicense(licenseId, e.data.shopName, e.data.shopKind, e.data.url, e.data.image));
    };

    const handleUpdate = (e) => {
        store.dispatch(updateShop(e.data));
    };

    const onVersionBlurd = (e) => {
        setSelectedVersion(e.value);
    };

    // Fallback-Image, falls keine Version gewaehlt und keine am Lizenz-Objekt gesetzt ist.
    const FALLBACK_CONTAINER_IMAGE = "gitlab.personalmanager.net:5050/foodgenius2/bios:release";

    const deployButtons = () => {
        if (deployStatus === 0) {
            return (
                <div style={{ display: "grid", gap: 10 }}>
                    <FormControl fullWidth>
                        <InputLabel>Version</InputLabel>
                        <Select
                            value={selectedVersion || license.containerImage || FALLBACK_CONTAINER_IMAGE}
                            onBlur={(e) => onVersionBlurd({ value: e.target.value })}
                        >
                            {availableVersions.map((version) => (
                                <MenuItem key={version} value={version}>
                                    {version}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button variant="contained" onClick={deployInstance} disabled>
                        Deploy
                    </Button>
                    <Button variant="contained" onClick={toggleChangeVersionPopup} disabled>
                        Version ändern
                    </Button>
                    {license && license.clusterStatus !== "deployed" && (
                        <Button
                            variant="contained"
                            onClick={() => {
                                store
                                    .dispatch(
                                        startInstance(
                                            "bios",
                                            license ? license.deployFrontendUrl.replaceAll(".", "-") : ""
                                        )
                                    )
                                    .then(() => {
                                        store.dispatch(getLicenseById(licenseId)).then((result) => {
                                            setLicense(result);
                                            setDeployStatus(result.Container ? result.deployStatus : 0);
                                            setDeployStatusString(result.Container ? result.deployStatusString : "");
                                            setSelectedVersion(result.containerImage);
                                        });
                                    })
                                    .catch((e) => {
                                        window.alert(getLicmanShopDeployAlertMessage(e));
                                    });
                            }}
                            disabled
                        >
                            Instanz starten
                        </Button>
                    )}
                    {license && license.clusterStatus === "deployed" && (
                        <Button
                            variant="contained"
                            onClick={() =>
                                store
                                    .dispatch(
                                        stopInstance(
                                            "bios",
                                            license ? license.deployFrontendUrl.replaceAll(".", "-") : ""
                                        )
                                    )
                                    .then(() => {
                                        store.dispatch(getLicenseById(licenseId)).then((result) => {
                                            setLicense(result);
                                            setDeployStatus(result.Container ? result.deployStatus : 0);
                                            setDeployStatusString(result.Container ? result.deployStatusString : "");
                                            setSelectedVersion(result.containerImage);
                                        });
                                    })
                                    .catch((e) => {
                                        window.alert(getLicmanShopDeployAlertMessage(e));
                                    })
                            }
                            disabled
                        >
                            Instanz stoppen
                        </Button>
                    )}
                    {license && license.Revoked === false && (
                        <Button variant="contained" color="error" onClick={() => toggleRevokeLicensePopup()} disabled>
                            Lizenz sperren
                        </Button>
                    )}
                    {license && license.Revoked && (
                        <Button variant="contained" color="error" onClick={() => toggleRevokeLicensePopup()} disabled>
                            Lizenz entsperren
                        </Button>
                    )}
                    <Button variant="contained" color="error" onClick={() => toggleDeleteInstancePopup()} disabled>
                        Instanz löschen
                    </Button>
                </div>
            );
        } else {
            return (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography>Deploy-Status: {deployStatusString}</Typography>
                        <Button variant="contained" onClick={getDeployStatus}>
                            Status aktualisieren
                        </Button>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <FormControl fullWidth>
                            <InputLabel>Version</InputLabel>
                            <Select value={selectedVersion} onBlur={(e) => onVersionBlurd({ value: e.target.value })}>
                                {availableVersions.map((version) => (
                                    <MenuItem key={version} value={version}>
                                        {version}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button variant="contained" onClick={toggleChangeVersionPopup} disabled>
                            Version ändern
                        </Button>
                    </div>
                </div>
            );
        }
    };

    const toggleRevokeLicensePopup = () => {
        setShowRevokeLicensePopup(!showRevokeLicensePopup);
    };

    const toggleDeleteInstancePopup = () => {
        setShowDeleteInstancePopup(!showDeleteInstancePopup);
    };

    const toggleChangeVersionPopup = () => {
        setShowChangeVersionPopup(!showChangeVersionPopup);
    };

    const onSelectionBlurd = (e) => {
        let modules = e.value.map((v) => {
            return { Module: v };
        });
        setLicense({
            ...license,
            ModulesBought: modules,
        });
    };

    const getDeployStatus = () => {
        store.dispatch(getDeployStatus(licenseId)).then((result) => {
            setDeployStatusString(result.deployStatusString);
            setDeployStatus(result.deployStatus);
        });
    };

    /**
     * Persists a license snapshot via FGFunctions updateLicense.
     *
     * @param {Object} [licenseToSave] - License payload to save; falls back to current state
     */
    const persistLicense = (licenseToSave) => {
        const payload = licenseToSave ?? license;
        if (payload) {
            store.dispatch(updateLicense(payload)).then(() => {});
        }
    };

    /**
     * Updates one license field in React state and immediately persists the new snapshot.
     * Avoids stale-state saves from onBlur using a closed-over license object.
     *
     * @param {string} field - License field name
     * @param {*} value - New field value
     */
    const handleLicenseFieldCommit = (field, value) => {
        setLicense((prev) => {
            if (!prev) {
                return prev;
            }
            const next = { ...prev, [field]: value };
            persistLicense(next);
            return next;
        });
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === "clickaway") return;
        setSnackbar({ ...snackbar, open: false });
    };

    const showSnackbar = (message, severity = "info") => {
        setSnackbar({ open: true, message, severity });
    };

    const openBlsProgress = () => {
        if (!licenseId) {
            showSnackbar("Keine Lizenz-ID verfügbar", "error");
            return;
        }
        setShowBlsProgressPopup(true);
        setBlsProgressPollingActive(true);
        store
            .dispatch(getBlsImportProgress(licenseId))
            .then((progress) => {
                if (progress && progress.status) {
                    setBlsImportProgress(progress);
                    return;
                } else {
                    setBlsImportProgress(null);
                }
                if (!blsImportRunning) {
                    startBlsImport();
                }
            })
            .catch(() => {
                setBlsImportProgress(null);
                if (!blsImportRunning) {
                    startBlsImport();
                }
            });
    };

    const startBlsImport = () => {
        if (!licenseId) {
            showSnackbar("Keine Lizenz-ID verfügbar", "error");
            return;
        }
        setBlsImportProgress(null);
        setBlsImportRunning(true);
        store
            .dispatch(importBlsNutritionalValues(licenseId, overwriteBlsExisting))
            .then(() => {
                showSnackbar("BLS-Import abgeschlossen", "success");
            })
            .catch((error) => {
                showSnackbar(`Fehler beim BLS-Import: ${error.message || error}`, "error");
            })
            .finally(() => {
                setBlsImportRunning(false);
            });
    };

    React.useEffect(() => {
        if (!blsProgressPollingActive || !licenseId) {
            return;
        }

        let isMounted = true;
        const intervalId = setInterval(() => {
            store
                .dispatch(getBlsImportProgress(licenseId))
                .then((progress) => {
                    if (!isMounted) {
                        return;
                    }
                    if (progress && progress.status) {
                        setBlsImportProgress(progress);
                        if (progress.status === "completed" || progress.status === "failed") {
                            clearInterval(intervalId);
                        }
                    }
                })
                .catch(() => {
                    // Keep polling; errors can occur if progress is not ready yet.
                });
        }, 1000);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, [blsProgressPollingActive, licenseId]);

    const toggleBlsProgressPopup = () => {
        const nextOpen = !showBlsProgressPopup;
        setShowBlsProgressPopup(nextOpen);
        if (!nextOpen) {
            setBlsProgressPollingActive(false);
        } else {
            setBlsProgressPollingActive(true);
        }
    };

    const formatDuration = (totalSeconds) => {
        if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
            return "-";
        }
        const seconds = Math.round(totalSeconds);
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        if (minutes > 0) {
            return `${minutes}m ${secs}s`;
        }
        return `${secs}s`;
    };

    const handleAcknowledgeTseIssue = () => {
        if (!license || !tseHealthCheckSucceeded) {
            return;
        }
        store.dispatch(clearLicenseTseIssueActive(licenseId)).then(() => {
            store.dispatch(getLicenseById(licenseId)).then((result) => {
                setLicense(result);
                setTseHealthCheckSucceeded(false);
                showSnackbar("TSE-Hinweis wurde quittiert.", "success");
            });
        });
    };

    const handleOpenTseHealthCheck = () => {
        if (!licenseId || !license?.tseIssueActive) {
            return;
        }
        setTseHealthCheckSucceeded(false);
        setTseHealthDialogOpen(true);
        setTseHealthResult(null);
        setTseHealthLoading(true);
        store
            .dispatch(runLicenseTseHealthCheck(licenseId))
            .then((data) => {
                if (data && Array.isArray(data.steps)) {
                    setTseHealthResult(data);
                    setTseHealthCheckSucceeded(data.ok === true);
                } else {
                    setTseHealthResult({
                        ok: false,
                        steps: [
                            {
                                id: "client",
                                ok: false,
                                label: "Server-Antwort",
                                detail: "Unerwartete oder leere Antwort (HTTP-Fehler?).",
                            },
                        ],
                    });
                    setTseHealthCheckSucceeded(false);
                }
            })
            .catch(() => {
                setTseHealthResult({
                    ok: false,
                    steps: [
                        {
                            id: "client",
                            ok: false,
                            label: "Netzwerk",
                            detail: "Anfrage fehlgeschlagen.",
                        },
                    ],
                });
                setTseHealthCheckSucceeded(false);
            })
            .finally(() => setTseHealthLoading(false));
    };

    const getImportTiming = (progress) => {
        if (!progress || !progress.startedAt) {
            return { elapsed: "-", eta: "-" };
        }
        const startedAt = new Date(progress.startedAt).getTime();
        const now = progress.lastUpdatedAt ? new Date(progress.lastUpdatedAt).getTime() : Date.now();
        const elapsedSeconds = Math.max(0, (now - startedAt) / 1000);
        const rate = progress.processed > 0 ? elapsedSeconds / progress.processed : null;
        const etaSeconds = rate ? rate * progress.remaining : null;
        return {
            elapsed: formatDuration(elapsedSeconds),
            eta: etaSeconds !== null ? formatDuration(etaSeconds) : "-",
        };
    };

    return (
        <div>
            {license && (
                <Stack spacing={3}>
                    {license.tseIssueActive === true && (
                        <Alert
                            severity="warning"
                            action={
                                tseHealthCheckSucceeded ? (
                                    <Button
                                        color="inherit"
                                        size="small"
                                        onClick={handleAcknowledgeTseIssue}
                                        data-testid="license-tse-issue-ack"
                                    >
                                        Quittieren
                                    </Button>
                                ) : (
                                    <Tooltip title="Führen Sie zuerst die TSE-Diagnose aus und schließen Sie sie ohne Fehler ab.">
                                        <span>
                                            <Button
                                                color="inherit"
                                                size="small"
                                                disabled
                                                data-testid="license-tse-issue-ack"
                                            >
                                                Quittieren
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )
                            }
                        >
                            Es liegt ein Fehler mit der technischen Sicherheitseinrichtung (TSE) vor. Quittieren ist
                            erst möglich, wenn die TSE-Diagnose vollständig erfolgreich war.
                        </Alert>
                    )}
                    {license.tseIssueActive === true && (
                        <>
                            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                                <Button
                                    variant="outlined"
                                    onClick={handleOpenTseHealthCheck}
                                    disabled={tseHealthLoading}
                                    data-testid="license-tse-health-run"
                                >
                                    TSE-Diagnose ausführen
                                </Button>
                                <Typography variant="body2" color="text.secondary">
                                    Prüft Sign-API, Anmeldung, Datenbank-Eintrag und TSS-Status bei Fiskaly.
                                </Typography>
                            </Stack>
                            <Dialog
                                open={tseHealthDialogOpen}
                                onClose={() => setTseHealthDialogOpen(false)}
                                maxWidth="md"
                                fullWidth
                                aria-labelledby="tse-health-dialog-title"
                            >
                                <DialogTitle id="tse-health-dialog-title">TSE-Diagnose</DialogTitle>
                                <DialogContent>
                                    {tseHealthLoading && (
                                        <Stack alignItems="center" py={2} spacing={1}>
                                            <CircularProgress size={32} aria-label="Diagnose läuft" />
                                            <Typography variant="body2" color="text.secondary">
                                                Verbindung zur Mandanten-Instanz und Fiskaly wird geprüft …
                                            </Typography>
                                        </Stack>
                                    )}
                                    {!tseHealthLoading && tseHealthResult && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Lizenz {tseHealthResult.licenseNumber ?? licenseId}
                                            {tseHealthResult.deployFrontendUrl
                                                ? ` · ${String(tseHealthResult.deployFrontendUrl)}`
                                                : ""}
                                        </Typography>
                                    )}
                                    {!tseHealthLoading && tseHealthResult && Array.isArray(tseHealthResult.steps) && (
                                        <Stack spacing={1.5}>
                                            {tseHealthResult.steps.map((step) => (
                                                <Alert
                                                    key={step.id}
                                                    severity={step.ok ? "success" : "error"}
                                                    icon={step.ok ? <CheckCircleIcon /> : <ErrorOutlinedIcon />}
                                                >
                                                    <Typography variant="subtitle2" component="span" display="block">
                                                        {step.label}
                                                    </Typography>
                                                    {step.detail ? (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ mt: 0.5, wordBreak: "break-word" }}
                                                        >
                                                            {step.detail}
                                                        </Typography>
                                                    ) : null}
                                                </Alert>
                                            ))}
                                        </Stack>
                                    )}
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setTseHealthDialogOpen(false)}>Schließen</Button>
                                </DialogActions>
                            </Dialog>
                        </>
                    )}
                    <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
                        <Paper elevation={3} sx={{ p: 3, flex: 1 }}>
                            <Stack spacing={3}>
                                <Typography variant="h6">Allgemein</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!license.hasFreeAccess}
                                            onChange={(e) =>
                                                handleLicenseFieldCommit("hasFreeAccess", e.target.checked)
                                            }
                                        />
                                    }
                                    label="Kostenloser Zugang zu allen Modulen"
                                />
                                <Stack direction="row" spacing={3}>
                                    <Stack spacing={4} sx={{ width: "75%" }}>
                                        <TextField
                                            fullWidth
                                            label="Name"
                                            value={license.StoreName}
                                            onChange={(e) => setLicense({ ...license, StoreName: e.target.value })}
                                            onBlur={(e) => handleLicenseFieldCommit("StoreName", e.target.value)}
                                            required
                                        />
                                        <TextField
                                            fullWidth
                                            label="Inhaber / GF"
                                            value={license.Owner}
                                            onChange={(e) => setLicense({ ...license, Owner: e.target.value })}
                                            onBlur={(e) => handleLicenseFieldCommit("Owner", e.target.value)}
                                            required
                                        />
                                        <FormControl fullWidth>
                                            <InputLabel>Unternehmenskategorie</InputLabel>
                                            <Select
                                                label="Unternehmenskategorie"
                                                value={license.CompanyType}
                                                onChange={(e) => {
                                                    handleLicenseFieldCommit("CompanyType", e.target.value);
                                                }}
                                            >
                                                {CATEGORIES.map((category) => (
                                                    <MenuItem key={category} value={category}>
                                                        {category}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            fullWidth
                                            label="Lizenznummer"
                                            value={license.LicenseNumber}
                                            disabled
                                        />
                                        <TextField
                                            fullWidth
                                            label="Unternehmensschlüssel"
                                            value={license.companyKey}
                                            disabled
                                        />
                                        <LocalizationProvider dateAdapter={AdapterMoment}>
                                            <DatePicker
                                                label="Lizenzablauf"
                                                value={moment(license.ValidityDate)}
                                                onChange={(newValue) => {
                                                    if (!newValue) {
                                                        return;
                                                    }
                                                    handleLicenseFieldCommit(
                                                        "ValidityDate",
                                                        newValue.format("YYYY-MM-DD")
                                                    );
                                                }}
                                                renderInput={(params) => <TextField {...params} />}
                                            />
                                        </LocalizationProvider>
                                        <TextField
                                            fullWidth
                                            label="Cluster-Status"
                                            value={license.clusterStatus}
                                            InputProps={{ readOnly: true }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Cluster-Version"
                                            value={license.clusterVersion}
                                            InputProps={{ readOnly: true }}
                                        />
                                    </Stack>
                                    <Stack spacing={3} sx={{ width: "25%" }}>
                                        {deployButtons()}
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Paper>
                        <Paper elevation={3} sx={{ p: 3, flex: 1 }}>
                            <Typography variant="h6" gutterBottom>
                                Informationen zum Inhaber
                            </Typography>
                            <Stack direction="row" spacing={3}>
                                <Stack spacing={3} sx={{ width: "50%" }}>
                                    <TextField
                                        fullWidth
                                        label="Titel"
                                        value={license.title}
                                        onChange={(e) => setLicense({ ...license, title: e.target.value })}
                                        onBlur={(e) => handleLicenseFieldCommit("title", e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Vorname"
                                        value={license.ownerFirstName || ""}
                                        onChange={(e) => setLicense({ ...license, ownerFirstName: e.target.value })}
                                        onBlur={(e) => handleLicenseFieldCommit("ownerFirstName", e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Nachname"
                                        value={license.ownerLastName}
                                        onChange={(e) => setLicense({ ...license, ownerLastName: e.target.value })}
                                        onBlur={(e) => handleLicenseFieldCommit("ownerLastName", e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Straße"
                                        value={license.Street}
                                        onChange={(e) => setLicense({ ...license, Street: e.target.value })}
                                        onBlur={(e) => handleLicenseFieldCommit("Street", e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Hausnummer"
                                        value={license.houseNumber}
                                        onChange={(e) => setLicense({ ...license, houseNumber: e.target.value })}
                                        onBlur={(e) => handleLicenseFieldCommit("houseNumber", e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Adresszusatz"
                                        value={license.AddressDetails}
                                        onChange={(e) => setLicense({ ...license, AddressDetails: e.target.value })}
                                        onBlur={(e) => handleLicenseFieldCommit("AddressDetails", e.target.value)}
                                    />
                                </Stack>
                                <Stack spacing={3} sx={{ width: "50%" }}>
                                    <TextField
                                        fullWidth
                                        label="Postleitzahl"
                                        value={license.Zip}
                                        onChange={(e) => setLicense({ ...license, Zip: e.target.value })}
                                        onBlur={(e) => handleLicenseFieldCommit("Zip", e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Stadt"
                                        value={license.City}
                                        onChange={(e) => setLicense({ ...license, City: e.target.value })}
                                        onBlur={(e) => handleLicenseFieldCommit("City", e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Telefonnummer"
                                        value={license.PhoneNumber || ""}
                                        onChange={(e) => setLicense({ ...license, PhoneNumber: e.target.value })}
                                        onBlur={(e) => handleLicenseFieldCommit("PhoneNumber", e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Breitengrad"
                                        type="number"
                                        inputProps={{ step: "any" }}
                                        value={license.latitude ?? ""}
                                        onChange={(e) =>
                                            setLicense({
                                                ...license,
                                                latitude: e.target.value === "" ? "" : Number(e.target.value),
                                            })
                                        }
                                        onBlur={(e) => {
                                            const parsed = Number(e.target.value);
                                            handleLicenseFieldCommit("latitude", Number.isFinite(parsed) ? parsed : 0);
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Längengrad"
                                        type="number"
                                        inputProps={{ step: "any" }}
                                        value={license.longitude ?? ""}
                                        onChange={(e) =>
                                            setLicense({
                                                ...license,
                                                longitude: e.target.value === "" ? "" : Number(e.target.value),
                                            })
                                        }
                                        onBlur={(e) => {
                                            const parsed = Number(e.target.value);
                                            handleLicenseFieldCommit("longitude", Number.isFinite(parsed) ? parsed : 0);
                                        }}
                                    />
                                </Stack>
                            </Stack>
                            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                                Rechtliche Texte
                            </Typography>
                            <Stack spacing={3}>
                                <TextField
                                    fullWidth
                                    label="Datenschutzvereinbarung"
                                    value={license.dataProtectionPolicy}
                                    onChange={(e) => setLicense({ ...license, dataProtectionPolicy: e.target.value })}
                                    onBlur={(e) => handleLicenseFieldCommit("dataProtectionPolicy", e.target.value)}
                                />
                                <TextField
                                    fullWidth
                                    label="Impressum"
                                    value={license.impressum || ""}
                                    onChange={(e) => setLicense({ ...license, impressum: e.target.value })}
                                    onBlur={(e) => handleLicenseFieldCommit("impressum", e.target.value)}
                                />
                            </Stack>
                        </Paper>
                    </Stack>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
                        <Paper elevation={3} sx={{ p: 3, flex: 1 }}>
                            <Typography variant="h6" gutterBottom>
                                Datenbank
                            </Typography>
                            <Stack direction="row" spacing={3}>
                                <Stack spacing={3} sx={{ width: "50%" }}>
                                    <TextField
                                        fullWidth
                                        label="Datenbank Server"
                                        value={license.databaseServer}
                                        onChange={(e) => setLicense({ ...license, databaseServer: e.target.value })}
                                        onBlur={(e) => handleLicenseFieldCommit("databaseServer", e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Datenbank Benutzer"
                                        value={license.databaseUser}
                                        onChange={(e) => setLicense({ ...license, databaseUser: e.target.value })}
                                        onBlur={(e) => handleLicenseFieldCommit("databaseUser", e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Container Image"
                                        value={license.containerImage}
                                        onChange={(e) => setLicense({ ...license, containerImage: e.target.value })}
                                        onBlur={(e) => handleLicenseFieldCommit("containerImage", e.target.value)}
                                    />
                                </Stack>
                                <Stack spacing={3} sx={{ width: "50%" }}>
                                    <TextField
                                        fullWidth
                                        label="Datenbank Name"
                                        value={license.databaseName}
                                        onChange={(e) => setLicense({ ...license, databaseName: e.target.value })}
                                        onBlur={(e) => handleLicenseFieldCommit("databaseName", e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Datenbank Passwort"
                                        value={license.databasePassword}
                                        onChange={(e) => setLicense({ ...license, databasePassword: e.target.value })}
                                        onBlur={(e) => handleLicenseFieldCommit("databasePassword", e.target.value)}
                                    />
                                    <Button variant="contained" onClick={openBlsProgress}>
                                        BLS-Import anzeigen
                                    </Button>
                                </Stack>
                            </Stack>
                        </Paper>{" "}
                        <Paper elevation={3} sx={{ p: 3, flex: 1 }}>
                            <Typography variant="h6" gutterBottom>
                                URLs
                            </Typography>
                            <Stack direction="row" spacing={3}>
                                <Stack spacing={3} sx={{ width: "50%" }}>
                                    <TextField
                                        fullWidth
                                        label="Deploy-Frontend-Url"
                                        value={license.deployFrontendUrl || ""}
                                        onChange={(e) => setLicense({ ...license, deployFrontendUrl: e.target.value })}
                                        onBlur={(e) => handleLicenseFieldCommit("deployFrontendUrl", e.target.value)}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Shop-Frontend-Url"
                                        value={license.shopFrontendUrl}
                                        onChange={(e) => setLicense({ ...license, shopFrontendUrl: e.target.value })}
                                        onBlur={(e) => handleLicenseFieldCommit("shopFrontendUrl", e.target.value)}
                                    />
                                </Stack>
                                <Stack spacing={3} sx={{ width: "50%" }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={license.ratingEnabled}
                                                onChange={(e) =>
                                                    handleLicenseFieldCommit("ratingEnabled", e.target.checked)
                                                }
                                            />
                                        }
                                        label="Bewertungen aktiviert"
                                    />
                                </Stack>
                            </Stack>
                        </Paper>
                    </Stack>
                </Stack>
            )}
            <Dialog open={showRevokeLicensePopup} onClose={toggleRevokeLicensePopup}>
                <DialogTitle>Lizenz sperren</DialogTitle>
                <DialogContent>
                    <TextField
                        multiline
                        rows={4}
                        fullWidth
                        placeholder="Grund für die Sperrung"
                        value={license ? license.RevokedReason : ""}
                        onBlur={(e) => setRevokedReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={toggleRevokeLicensePopup}>Abbrechen</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => {
                            if (license && !license.Revoked) {
                                store.dispatch(revokeLicense(licenseId, revokedReason));
                            } else {
                                store.dispatch(reactivateLicense(licenseId));
                            }
                        }}
                    >
                        {license && !license.Revoked ? "Lizenz sperren" : "Lizenz entsperren"}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={showDeleteInstancePopup} onClose={toggleDeleteInstancePopup}>
                <DialogTitle>Instanz löschen</DialogTitle>
                <DialogContent>
                    <Typography>Sind Sie sicher, dass Sie die Instanz löschen möchten?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={toggleDeleteInstancePopup}>Abbrechen</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => {
                            store
                                .dispatch(deleteInstance(licenseId))
                                .then(() => {
                                    showSnackbar("Instanz erfolgreich gelöscht", "success");
                                    navigate("/lizenzen");
                                })
                                .catch((error) => {
                                    showSnackbar("Fehler beim Löschen der Instanz: " + error.message, "error");
                                });
                            toggleDeleteInstancePopup();
                        }}
                    >
                        Instanz löschen
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={showChangeVersionPopup} onClose={toggleChangeVersionPopup}>
                <DialogTitle>Version ändern</DialogTitle>
                <DialogContent>
                    <Typography>Achtung: Das Ändern der Version führt zu einem Neustart der Instanz.</Typography>
                    <Typography>Möchten Sie fortfahren?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={toggleChangeVersionPopup}>Abbrechen</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            store.dispatch(changeImageVersion(licenseId, selectedVersion));
                            toggleChangeVersionPopup();
                        }}
                    >
                        Version ändern
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={showBlsProgressPopup} onClose={toggleBlsProgressPopup}>
                <DialogTitle>BLS-Import Fortschritt</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ minWidth: 320 }}>
                        {blsImportProgress ? (
                            <>
                                <Typography>
                                    Importiert: {blsImportProgress.processed} / {blsImportProgress.totalRows}
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={
                                        blsImportProgress.totalRows > 0
                                            ? (blsImportProgress.processed / blsImportProgress.totalRows) * 100
                                            : 0
                                    }
                                    sx={{ height: 10, borderRadius: 5 }}
                                />
                                {(() => {
                                    const timing = getImportTiming(blsImportProgress);
                                    return (
                                        <>
                                            <Typography>Vergangen: {timing.elapsed}</Typography>
                                            <Typography>ETA: {timing.eta}</Typography>
                                        </>
                                    );
                                })()}
                            </>
                        ) : (
                            <>
                                <Typography>Import wird gestartet...</Typography>
                                <LinearProgress sx={{ height: 10, borderRadius: 5 }} />
                            </>
                        )}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={overwriteBlsExisting}
                                    onChange={(e) => setOverwriteBlsExisting(e.target.checked)}
                                />
                            }
                            label="Vorhandene Einträge überschreiben"
                        />
                        {blsImportProgress && (
                            <>
                                <Typography>Erstellt: {blsImportProgress.created}</Typography>
                                <Typography>Aktualisiert: {blsImportProgress.updated}</Typography>
                                <Typography>Übersprungen: {blsImportProgress.skipped}</Typography>
                                <Typography>Fehler: {blsImportProgress.errors}</Typography>
                                <Typography>Verbleibend: {blsImportProgress.remaining}</Typography>
                                <Typography>Status: {blsImportProgress.status}</Typography>
                            </>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    {!blsImportRunning && (
                        <Button variant="contained" onClick={startBlsImport}>
                            BLS importieren
                        </Button>
                    )}
                    <Button onClick={toggleBlsProgressPopup}>Schließen</Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default License;
